import { ReactNode, useState } from 'react'

import { Text } from '@/components/ui/text'
import { TooltipInfo } from '@/components/ui/tooltip-v2'
import SlippageSetting from '@/containers/home/settings/SlippageSetting'
import { useSlippageSettings } from '@/hooks/use-slippage'
import { CommonInfo } from '@/icons'
import { cn } from '@/lib'

import { useTranslation } from 'react-i18next'

const Label = ({ label, tooltip }: { label: string; tooltip: string }) => {
  return (
    <Text color='white-48' className='flex items-center gap-1'>
      {label}
      <TooltipInfo content={tooltip} icon={<CommonInfo className='size-5' />} />
    </Text>
  )
}

enum SlippageTab {
  AUTO,
  CUSTOM,
}

const Tabs = ({
  options,
  tab,
  setTab,
  slippage,
  suggestSlippage,
}: {
  options: { id: SlippageTab; label: string }[]
  tab: SlippageTab
  setTab: (v: SlippageTab) => void
  slippage: number
  suggestSlippage: number
}) => {
  return (
    <div className='p-1 flex rounded-sm ring-[1px] md:ring-[0.5px] [@media(max-resolution:1.9dppx)]:ring-[1px] ring-white-10 gap-1'>
      {options.map((opt) => {
        const selected = tab === opt.id
        return (
          <div
            key={opt.id}
            className={cn('h-10 flex items-center cursor-pointer select-none py-1 px-6 rounded-sm', {
              'bg-white-8 ': selected,
              'hover:bg-white-12': !selected,
            })}
            onClick={() => setTab(opt.id)}
          >
            <Text size={16}>
              {opt.label} {selected ? (opt.id === SlippageTab.AUTO ? `${suggestSlippage}%` : `${slippage}%`) : null}
            </Text>
          </div>
        )
      })}
    </div>
  )
}

const slippageTabs = [
  { id: SlippageTab.AUTO, label: `Auto` },
  { id: SlippageTab.CUSTOM, label: 'Custom' },
]

const SlippageContent = ({
  label,
  warning,
  realtime,
  isFixedSwap,
  isBridge,
}: {
  label?: ReactNode
  warning?: boolean
  realtime?: boolean
  isFixedSwap: boolean
  isBridge: boolean
}) => {
  const { t } = useTranslation()
  const { slippage, setSlippage, suggestSlippage, isCustomSlippage, setIsCustomSlippage } = useSlippageSettings({
    isFixedSwap,
    isBridge,
  })

  const [tab, setTab] = useState(!isCustomSlippage ? SlippageTab.AUTO : SlippageTab.CUSTOM)
  return (
    <>
      <div className='flex justify-between items-center'>
        {label || <Label label='Slippage' tooltip={t('tooltip.SLIPPAGE')} />}
        <Tabs
          options={slippageTabs}
          setTab={(tab) => {
            setTab(tab)
            setIsCustomSlippage(tab === SlippageTab.CUSTOM)
            if (tab === SlippageTab.AUTO) {
              setSlippage(suggestSlippage)
            }
          }}
          tab={tab}
          suggestSlippage={suggestSlippage}
          slippage={slippage}
        />
      </div>

      <SlippageSetting
        isOpen={tab === SlippageTab.CUSTOM}
        warning={warning}
        realtime={realtime}
        fixedSwap={!!isFixedSwap}
        isBridge={isBridge}
      />
    </>
  )
}

export default function Settings({ isFixedSwap, isBridge }: { isFixedSwap: boolean; isBridge: boolean }) {
  const { t } = useTranslation()
  return (
    <div className='flex flex-col pt-2'>
      <SlippageContent isFixedSwap={isFixedSwap} isBridge={isBridge} />

      <div className='flex justify-between mt-6'>
        <Label label='Swap expiry' tooltip={t('tooltip.SWAP_EXPIRY')} />
        <Text as='span' size={16}>
          24 Hours
        </Text>
      </div>
    </div>
  )
}
